package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import kotlin.jvm.JvmStatic

/**
 * 上拉加载装饰器：当 body 滚动到距底部小于 [threshold] 时触发 [onLoadMore]。
 *
 * 触发条件每帧检查（[FrameTickable]）：`maxScrollY - scrollY < threshold`。[loading] 锁防止重复触发——
 * 触发后置 true，业务加载完调用 [finishLoad] 解锁并更新 [hasMore]。hasMore=false 时不再触发。
 *
 * 本类只负责「触底检测 + 防重」，不直接发起加载；加载逻辑由 [onLoadMore] 回调（IR 层把 DSL fun 包成
 * EffectBody 注入）执行。
 */
class InfiniteScroll private constructor() :
    ScrollDecorator<InfiniteScroll>(),
    FrameTickable {

    private var thresholdValue: Float = DEFAULT_THRESHOLD
    private var loadingFlag: Boolean = false
    private var hasMoreFlag: Boolean = true
    private var loadingTextValue: String = "加载中..."
    private var noMoreTextValue: String = "已是最后"
    private var onLoadMoreCallback: EffectBody? = null

    protected override fun self(): InfiniteScroll = this

    // -------- 字段 --------

    fun threshold(px: Float): InfiniteScroll {
        thresholdValue = maxOf(0f, px)
        return this
    }

    fun threshold(): Float = thresholdValue

    fun hasMore(has: Boolean): InfiniteScroll {
        hasMoreFlag = has
        return this
    }

    fun hasMore(): Boolean = hasMoreFlag

    fun loading(): Boolean = loadingFlag

    fun loadingText(t: String?): InfiniteScroll {
        loadingTextValue = t ?: ""
        return this
    }

    fun loadingText(): String = loadingTextValue

    fun noMoreText(t: String?): InfiniteScroll {
        noMoreTextValue = t ?: ""
        return this
    }

    fun noMoreText(): String = noMoreTextValue

    /** 设置加载回调（IR 层把 DSL fun 包成 EffectBody 注入；common 无 Runnable，用既有 SAM）。 */
    fun onLoadMore(callback: EffectBody?): InfiniteScroll {
        onLoadMoreCallback = callback
        return this
    }

    // -------- 触底检测 --------

    /** 当前是否处于「距底部小于 threshold」的触发区（供测试/渲染读取）。 */
    fun atTriggerZone(): Boolean {
        val b = bodyField ?: return false
        val max = b.maxScrollY()
        // 内容尚未超出视窗（max<=0，可能未测量或本就无需滚动）时不触发，避免首帧误触发
        if (max <= 0f) {
            return false
        }
        val remaining = max - b.scrollY()
        return remaining < thresholdValue
    }

    /** [FrameTickable]：每帧检查触底并按需触发 onLoadMore。不直接改视觉，返回 false。 */
    override fun tickFrame(nowMs: Long): Boolean {
        maybeTrigger()
        return false
    }

    /**
     * 检查并触发加载：hasMore 且未加载中且已进入触发区时，加锁并执行回调。
     * 暴露为 public 便于测试直接驱动（不依赖帧调度）。
     */
    fun maybeTrigger() {
        if (!hasMoreFlag || loadingFlag || bodyField == null) {
            return
        }
        if (!atTriggerZone()) {
            return
        }
        loadingFlag = true
        onLoadMoreCallback?.run()
    }

    /**
     * 加载完成：解锁并更新是否还有更多。由业务在追加数据后调用。
     *
     * @param hasMore 是否还有更多数据
     */
    fun finishLoad(hasMore: Boolean): InfiniteScroll {
        loadingFlag = false
        hasMoreFlag = hasMore
        return this
    }

    companion object {
        /** 触底触发的默认距离阈值（像素）。 */
        private const val DEFAULT_THRESHOLD = 80f

        /** 创建一个上拉加载装饰器。 */
        @JvmStatic
        fun create(): InfiniteScroll = InfiniteScroll()
    }
}
